/**
 * 竞价大厅（进行中/预告/已结束 Tab + 卡片流）
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAuctions } from '@/api/auction';
import type { Auction } from '@/types';
import { formatMoney } from '@/utils/format';
import { cosUrl, imgFallback } from '@/utils/constants';
import Countdown from '@/components/common/Countdown';
import StatusTag from '@/components/common/StatusTag';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/auction.css';

const TABS = [
  { key: 'live', label: '竞价中', status: 1 },
  { key: 'preview', label: '预告', status: 0 },
  { key: 'ended', label: '已结束', status: undefined },
];

export default function AuctionHall() {
  const [tab, setTab] = useState('live');
  const [list, setList] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const active = TABS.find((t) => t.key === tab);
      const data = await listAuctions(active?.status);
      setList(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="auction-page">
      <PageHero eyebrow="LIVE AUCTION" title="竞价大厅" desc="英式拍卖 · 实时出价 · 防秒杀延长" />

      <div className="container">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
              {t.key === 'live' ? <span className="dot" /> : null}
            </button>
          ))}
        </div>

        {loading ? (
          <Loading rows={6} />
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : list.length === 0 ? (
          <Empty icon="🔨" title="暂无竞价" desc="敬请期待新一场竞价" />
        ) : (
          <div className="auction-grid">
            {list.map((a) => {
              const live = a.status === 1;
              return (
                <Link className={`auction-card ${live ? 'auction-card--live' : ''}`} to={`/auction/${a.id}`} key={a.id}>
                  <div className="auction-card__cover">
                    <img
                      src={a.cattle?.images?.[0] ?? a.cattle?.face_image_url ?? cosUrl('/images/cattle-default.jpg')}
                      alt={a.cattle?.breed}
                      data-local="/images/cattle-default.jpg"
                      loading="lazy"
                      onError={imgFallback}
                    />
                    <StatusTag type="auction" status={a.status} />
                  </div>
                  <div className="auction-card__body">
                    <div className="auction-card__head">
                      <strong>{a.cattle?.breed} · {a.cattle?.ear_tag_no}</strong>
                      <span className="auction-card__bids">{a.bid_count} 次出价</span>
                    </div>
                    <div className="auction-card__price">
                      <span>
                        起拍 <b>{formatMoney(a.base_price)}</b>
                      </span>
                      <span>
                        当前 <b className="current">{formatMoney(a.current_price)}</b>
                      </span>
                    </div>
                    <div className="auction-card__foot">
                      <span>最小加价 {formatMoney(a.bid_increment)}</span>
                      {live ? (
                        <Countdown endTime={a.end_time} tone="gold" />
                      ) : (
                        <StatusTag type="auction" status={a.status} />
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
